import React, { useCallback, useEffect, useRef, useState } from 'react';

const COMMIT_DELAY = 500;

/**
 * QuickNotesBox — the mission's tiny plaintext scratch pad (`task.quickNotes`).
 *
 * Deliberately a bare textarea, not a block editor: the mission's real
 * content lives in attached docs now ("Mission Docs Become Real Docs").
 * This is for the two-line "waiting on Bob / remember the coupon code"
 * kind of note that isn't worth a document.
 */
const QuickNotesBox = ({ taskId, value, onQuickNotesChange }) => {
  const [text, setText] = useState(value || '');
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  /** Last value handed to the parent — distinguishes echoes from undo/redo */
  const lastSentRef = useRef(value || '');
  const onChangeRef = useRef(onQuickNotesChange);
  onChangeRef.current = onQuickNotesChange;
  const taskIdRef = useRef(taskId);
  taskIdRef.current = taskId;

  // External change (undo/redo) — adopt it unless it's our own echo
  useEffect(() => {
    const external = value || '';
    if (external === lastSentRef.current) return;
    lastSentRef.current = external;
    pendingRef.current = null;
    clearTimeout(timerRef.current);
    setText(external);
  }, [value]);

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (pendingRef.current === null) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    lastSentRef.current = next;
    onChangeRef.current?.(taskIdRef.current, next);
  }, []);

  const handleChange = (e) => {
    const next = e.target.value;
    setText(next);
    pendingRef.current = next;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, COMMIT_DELAY);
  };

  // Flush on unmount (modal close, mission switch)
  useEffect(() => flush, [flush]);

  return (
    <div className="mfv-quick-notes">
      <div className="mfv-quick-notes__label">Quick notes</div>
      <textarea
        className="mfv-quick-notes__input"
        value={text}
        onChange={handleChange}
        onBlur={flush}
        placeholder="Scratch space — plain text only. Anything bigger belongs in a doc below."
      />
    </div>
  );
};

export default QuickNotesBox;
